import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { HomePage } from './pages/HomePage';
import { ShopPage } from './pages/ShopPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { WishlistPage } from './pages/WishlistPage';
import { OrdersPage } from './pages/OrdersPage';
import { VendorDashboard } from './pages/VendorDashboard';
import { AdminDashboard } from './pages/AdminDashboard';
import { BecomeSellerPage } from './pages/BecomeSellerPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { AccountPage } from './pages/AccountPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { MessagesPage } from './pages/MessagesPage';
import { AdminVouchersPage } from './pages/AdminVouchersPage';
import { VendorAnalyticsPage } from './pages/VendorAnalyticsPage';
import { PricingPage } from './pages/PricingPage';
import { MyQuotesPage } from './pages/MyQuotesPage';
import {
  AboutPage, ContactPage, HelpCenterPage, FAQPage, TrackOrderPage,
  CareersPage, PressPage, SellerCenterPage, SellerResourcesPage,
} from './pages/InfoPages';
import { AffiliateProgramPage, AffiliateDashboardPage } from './pages/AffiliatePages';
import { AffiliateLeaderboardPage } from './pages/AffiliateLeaderboardPage';
import { PressReleasesListPage, PressReleaseDetailPage } from './pages/PressReleasePages';
import { TrackOrderDetailPage } from './pages/TrackOrderDetailPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages';
import { AffiliateTracker } from './components/AffiliateTracker';
import { PromoCatcher } from './components/PromoCatcher';
import { CurrencyProvider } from './components/CurrencySelector';
import {
  LegalNoticePage,
  TermsPage,
  PrivacyPage,
  CookiesPage,
  ReturnsPage,
} from './pages/LegalPages';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F4C5C]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Layout Component
const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

// App Content with Routes
const AppContent = () => {
  return (
    <BrowserRouter>
      <AffiliateTracker />
      <PromoCatcher />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/categories" element={<Layout><CategoriesPage /></Layout>} />
        <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />        <Route path="/shop" element={<Layout><ShopPage /></Layout>} />
        <Route path="/product/:id" element={<Layout><ProductDetailPage /></Layout>} />
        <Route path="/login" element={<Layout><LoginPage /></Layout>} />
        <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
        <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />
        <Route path="/reset-password" element={<Layout><ResetPasswordPage /></Layout>} />
        <Route path="/cart" element={<Layout><CartPage /></Layout>} />
        <Route path="/become-seller" element={<Layout><BecomeSellerPage /></Layout>} />

        {/* Legal pages */}
        <Route path="/legal" element={<Layout><LegalNoticePage /></Layout>} />
        <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
        <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/cookies" element={<Layout><CookiesPage /></Layout>} />
        <Route path="/returns" element={<Layout><ReturnsPage /></Layout>} />

        {/* Info / footer pages */}
        <Route path="/about" element={<Layout><AboutPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/help" element={<Layout><HelpCenterPage /></Layout>} />
        <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
        <Route path="/track" element={<Layout><TrackOrderPage /></Layout>} />
        <Route path="/track/:orderId" element={<Layout><TrackOrderDetailPage /></Layout>} />
        <Route path="/careers" element={<Layout><CareersPage /></Layout>} />
        <Route path="/press" element={<Layout><PressPage /></Layout>} />
        <Route path="/press/releases" element={<Layout><PressReleasesListPage /></Layout>} />
        <Route path="/press/releases/:slug" element={<Layout><PressReleaseDetailPage /></Layout>} />
        <Route path="/seller-center" element={<Layout><SellerCenterPage /></Layout>} />
        <Route path="/seller-pricing" element={<Navigate to="/pricing" replace />} />
        <Route path="/seller-resources" element={<Layout><SellerResourcesPage /></Layout>} />

        {/* Affiliate program */}
        <Route path="/affiliate-program" element={<Layout><AffiliateProgramPage /></Layout>} />
        <Route path="/affiliates/leaderboard" element={<Layout><AffiliateLeaderboardPage /></Layout>} />
        <Route path="/affiliate" element={
          <ProtectedRoute>
            <Layout><AffiliateDashboardPage /></Layout>
          </ProtectedRoute>
        } />

        {/* Payment return flow */}
        <Route path="/payment/success" element={
          <ProtectedRoute>
            <Layout><PaymentSuccessPage /></Layout>
          </ProtectedRoute>
        } />

        {/* Protected Routes - Any authenticated user */}
        <Route path="/wishlist" element={
          <ProtectedRoute>
            <Layout><WishlistPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute>
            <Layout><OrdersPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute>
            <Layout><AccountPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/quotes" element={
          <ProtectedRoute>
            <Layout><MyQuotesPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute>
            <Layout><MessagesPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/messages/:conversationId" element={
          <ProtectedRoute>
            <Layout><MessagesPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/admin/vouchers" element={
          <ProtectedRoute requiredRole="admin">
            <Layout><AdminVouchersPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/vendor/analytics" element={
          <ProtectedRoute requiredRole="vendor">
            <Layout><VendorAnalyticsPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/checkout" element={
          <ProtectedRoute>
            <Layout><CheckoutPage /></Layout>
          </ProtectedRoute>
        } />
        
        {/* Vendor Routes */}
        <Route path="/vendor" element={
          <ProtectedRoute allowedRoles={['vendor', 'admin']}>
            <Layout><VendorDashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/vendor/*" element={
          <ProtectedRoute allowedRoles={['vendor', 'admin']}>
            <Layout><VendorDashboard /></Layout>
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/admin/*" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        } />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieConsentBanner />
    </BrowserRouter>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <CurrencyProvider>
            <AppContent />
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                style: {
                  fontFamily: 'Plus Jakarta Sans, sans-serif'
                }
              }}
            />
          </CurrencyProvider>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
